import React, { useState } from 'react';

import { AutomationTimelineItem } from './automation-timeline-item';
import { AutomationTimelineTitleCreate } from './automation-timeline-title-create';
import { MissingInterestConfirmationWarning } from './missing-interest-confirmation-warning';

import { isTruthy } from '../../../util/is-truthy';
import {
  APPLICANT_AUTOMATION_COMMUNICATION_TYPES,
  CreatableApplicantAutomationTypes,
} from '../../utils/applicant-automation-communication-types';
import {
  AutomationsByTriggerDictionary,
  getFollowUpsForTrigger,
} from '../../utils/applicant-automation-display-utils';
import { isApplicantAutomation } from '../../utils/is-applicant-automation';

type InputProps = {
  org: Org;
  editingForJob?: Job;
  trigger:
    | 'applicant'
    | CreatableApplicantAutomationTypes
    | StartDateTriggeredCommunicationTriggerType;
  title?: string;
  section?: 'allApplicantAutomations' | 'application' | 'hiring' | 'onboarding';
  isFirst: boolean;
  automations?: Array<ApplicantAutomation | StartDateTriggeredCommunication>;
  expandedForAutomationId?: number;
  interval?: EmployeeAutomationSendInterval;
  parentAutomationsForSection?: AutomationsByTriggerDictionary;
  missingInterestConfirmations?: boolean;
  parserJob?: Job;
  user?: AuthenticatedUser;
  focusApplicantAutomation?: ApplicantAutomation | null;
};

const applicantAutomationViewIds = (automation: ApplicantAutomation) =>
  [automation.applicantTriggeredCommunicationId, automation.overrideForCommunicationId].filter(
    isTruthy,
  );

export const AutomationTimelineTriggerSection = (props: InputProps) => {
  const {
    org,
    editingForJob,
    trigger,
    title,
    section,
    isFirst,
    automations,
    interval,
    parentAutomationsForSection,
    missingInterestConfirmations,
    parserJob,
    user,
    focusApplicantAutomation,
  } = props;

  const [viewingAutomationIds, setViewingAutomationIds] = useState<Array<number>>(
    focusApplicantAutomation ? [focusApplicantAutomation.applicantTriggeredCommunicationId] : [],
  );

  // For applicant automations, we will keep track of both the ATC ID and the overrideForCommunicationId.
  // This way, if we create a job override or are viewing job overrides, we can maintain the expanded
  // view for the org defaults view as well.
  const automationViewingIds = (
    automation: ApplicantAutomation | StartDateTriggeredCommunication,
  ) =>
    isApplicantAutomation(automation)
      ? applicantAutomationViewIds(automation)
      : [automation.startDateTriggeredCommunicationId];

  // We will check if any relevant IDs are currently in viewingAutomationIds
  // For applicant automations, this is the ATC ID or the overrideForCommunicationId
  const isViewingAutomation = (automation: ApplicantAutomation | StartDateTriggeredCommunication) =>
    automationViewingIds(automation).some((id) => id && viewingAutomationIds.includes(id));

  const toggleIsViewing =
    (automation: ApplicantAutomation | StartDateTriggeredCommunication) => () => {
      if (isViewingAutomation(automation)) {
        setViewingAutomationIds(
          viewingAutomationIds.filter((id) => !automationViewingIds(automation).includes(id)),
        );
      } else {
        setViewingAutomationIds(viewingAutomationIds.concat(automationViewingIds(automation)));
      }
    };

  return (
    <div key={`automation-timeline-${trigger}`}>
      <AutomationTimelineTitleCreate
        org={org}
        editingForJob={editingForJob}
        triggerType={trigger}
        interval={interval}
        section={section}
        title={title}
        isFirst={isFirst}
        parentAutomationsForSection={parentAutomationsForSection}
      />
      {missingInterestConfirmations &&
        trigger === APPLICANT_AUTOMATION_COMMUNICATION_TYPES.INTEREST_CONFIRMATION && (
          <MissingInterestConfirmationWarning
            parserJob={parserJob}
            editingForJob={editingForJob}
            user={user}
            org={org}
            parentAutomationsForSection={parentAutomationsForSection}
          />
        )}
      {automations?.map((automation: ApplicantAutomation | StartDateTriggeredCommunication) =>
        isApplicantAutomation(automation) ? (
          <AutomationTimelineItem
            key={`automation-timeline-item-${section}-${automation.applicantTriggeredCommunicationId}`}
            automation={automation}
            org={org}
            editingForJob={editingForJob}
            section={section}
            followUpsForSection={getFollowUpsForTrigger(automations as Array<ApplicantAutomation>)}
            isViewing={isViewingAutomation(automation)}
            toggleIsViewing={toggleIsViewing(automation)}
            parentAutomationsForSection={parentAutomationsForSection}
            focusApplicantAutomation={focusApplicantAutomation}
          />
        ) : (
          <AutomationTimelineItem
            key={`automation-timeline-item-${section}-${automation.startDateTriggeredCommunicationId}`}
            automation={automation}
            org={org}
            section={section}
            isViewing={isViewingAutomation(automation)}
            toggleIsViewing={toggleIsViewing(automation)}
          />
        ),
      )}
    </div>
  );
};
